import React from "react";
import PropTypes from "prop-types";
import LangSwitcher from "../../LangSwitcher";
import defaultAuthImage from "../../../assets/images/auth-screen/auth-container.jpg";
import logo from "../../../assets/images/auth-screen/yoram-big-logo.svg";
import "./AuthLayout.scss";

/**
 * Reusable AuthLayout - Split-screen layout for auth flows.
 * Left: full-height branding image (40-50% width on desktop).
 * Right: centered card-like content area (forms).
 * Mobile: left hidden, content full width.
 */
const AuthLayout = ({
  children,
  content,
  imageSrc,
  variant,
  headerEndExtra,
  centerForm,
  centerFormWide,
}) => {
  const displayContent = children ?? content;
  const bgImage = imageSrc ?? defaultAuthImage;
  const imageStyle = { backgroundImage: `url(${bgImage})` };
  const isOnboarding = variant === "onboarding";

  return (
    <div className={`auth-layout ${isOnboarding ? "auth-layout--onboarding" : ""}`}>
      <div className="auth-layout__inner">
        <div
          className="auth-layout__image-section"
          style={imageStyle}
          role="img"
          aria-label="Branding"
        />
        <div
          className={`auth-layout__content-section ${
            isOnboarding ? "auth-layout__content-section--onboarding" : ""
          } ${centerForm ? "auth-layout__content-section--center-form" : ""} ${
            centerFormWide ? "auth-layout__content-section--center-form-wide" : ""
          }`}
        >
          <div className="auth-layout__header d-flex justify-content-between align-items-center">
            <div className="auth-layout__logo">
              <img
                src={logo}
                alt="Yoram"
                className="auth-layout__logo-img"
              />
            </div>
            <div className="auth-layout__header-end">
              {headerEndExtra}
              <LangSwitcher variant="auth" />
            </div>
          </div>
          <div
            className={`auth-layout__card ${
              isOnboarding ? "auth-layout__card--onboarding" : ""
            }`}
          >
            {displayContent}
          </div>
        </div>
      </div>
    </div>
  );
};

AuthLayout.propTypes = {
  /** Main content (forms, etc.) - preferred over content */
  children: PropTypes.node,
  /** Alternative prop for backward compatibility */
  content: PropTypes.node,
  /** Optional image URL for left branding - uses project default if not provided */
  imageSrc: PropTypes.string,
  /** Layout variant: "onboarding" only for `/company/signup` welcome screen */
  variant: PropTypes.oneOf(["onboarding"]),
  /** Renders before the language switch in the header (e.g. company pending logout) */
  headerEndExtra: PropTypes.node,
  /** Vertically center compact auth forms (login, forgot password, etc.) */
  centerForm: PropTypes.bool,
  /** Wider centered card (e.g. client company selection) */
  centerFormWide: PropTypes.bool,
};

AuthLayout.defaultProps = {
  children: null,
  content: null,
  imageSrc: null,
  variant: null,
  headerEndExtra: null,
  centerForm: false,
  centerFormWide: false,
};

export default AuthLayout;
